// journal-scan-report.ts
//
// Quét `journal.jsonl` và phân loại theo §3.4:
//
//   clean        — mọi byte đều thuộc record đủ newline + hợp lệ.
//   partial_tail — prefix hợp lệ, đuôi CHƯA kết thúc newline (write đang dở).
//   corrupt      — có record ĐỦ newline nhưng sai JSON/checksum/seq ⇒ RECOVERING.
//
// Luật cứng: chỉ đuôi CHƯA có newline mới được coi là "write dở". Record đủ newline
// mà hỏng là bằng chứng mất dữ liệu ⇒ KHÔNG skip, KHÔNG tự sửa; con người quyết.

import { promises as fs } from "node:fs";
import { verifyRecordChecksum } from "./record-checksum.js";

export type JournalScanStatus = "clean" | "partial_tail" | "corrupt";

export type JournalProblemKind = "bad_json" | "bad_checksum" | "seq_gap" | "seq_duplicate";

export interface JournalRecord {
  seq: number;
  event_id: string;
  operation_id?: string;
  expected_revision?: number;
  delta: Record<string, unknown>;
  event_checksum: string;
  [key: string]: unknown;
}

export interface JournalScanReport {
  status: JournalScanStatus;
  /** Offset byte NGAY SAU record hợp lệ cuối cùng — điểm truncate an toàn. */
  lastValidOffset: number;
  lastSeq: number;
  /** Byte của đuôi chưa kết thúc newline (chỉ khi `partial_tail`). */
  tail?: Buffer;
  problem?: { offset: number; seq?: number; kind: JournalProblemKind; detail?: string };
  /** Record hợp lệ đã parse, theo thứ tự — dùng để replay/rebuild projection. */
  records: JournalRecord[];
}

function isRecordShape(value: unknown): value is JournalRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return typeof record.seq === "number" && Number.isInteger(record.seq);
}

/**
 * Quét journal. KHÔNG BAO GIỜ tự sửa file — chỉ báo cáo. Việc sửa (lưu forensic +
 * truncate) là của `recovery-reconciler.ts`, và chỉ với `partial_tail`.
 */
export async function scanJournal(journalPath: string): Promise<JournalScanReport> {
  let content: Buffer;
  try {
    content = await fs.readFile(journalPath);
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === "ENOENT") {
      return { status: "clean", lastValidOffset: 0, lastSeq: 0, records: [] };
    }
    throw err;
  }

  const records: JournalRecord[] = [];
  let offset = 0;
  let lastSeq = 0;

  while (offset < content.length) {
    const newlineAt = content.indexOf(0x0a, offset);
    if (newlineAt === -1) {
      // Đuôi chưa kết thúc newline: ứng viên DUY NHẤT của "write đang dở".
      return {
        status: "partial_tail",
        lastValidOffset: offset,
        lastSeq,
        tail: content.subarray(offset),
        records,
      };
    }

    const lineBytes = content.subarray(offset, newlineAt);
    const recordStart = offset;
    const text = lineBytes.toString("utf8");

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      return corrupt(records, recordStart, lastSeq, {
        kind: "bad_json",
        detail: (err as Error).message,
      });
    }

    if (!isRecordShape(parsed)) {
      return corrupt(records, recordStart, lastSeq, {
        kind: "bad_json",
        detail: "record thiếu field `seq` kiểu số nguyên",
      });
    }

    if (!verifyRecordChecksum(parsed as unknown as Record<string, unknown>)) {
      return corrupt(records, recordStart, lastSeq, { kind: "bad_checksum", seq: parsed.seq });
    }

    const expected = lastSeq + 1;
    if (parsed.seq !== expected) {
      const kind: JournalProblemKind = parsed.seq <= lastSeq ? "seq_duplicate" : "seq_gap";
      return corrupt(records, recordStart, lastSeq, {
        kind,
        seq: parsed.seq,
        detail: `mong đợi seq ${expected}, gặp ${parsed.seq}`,
      });
    }

    records.push(parsed);
    lastSeq = parsed.seq;
    offset = newlineAt + 1;
  }

  return { status: "clean", lastValidOffset: offset, lastSeq, records };
}

function corrupt(
  records: JournalRecord[],
  offset: number,
  lastSeq: number,
  problem: { kind: JournalProblemKind; seq?: number; detail?: string },
): JournalScanReport {
  return {
    status: "corrupt",
    lastValidOffset: offset,
    lastSeq,
    problem: { offset, ...problem },
    records,
  };
}
